import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Save, Trash2, Upload } from "lucide-react";
import { RequireAdmin, AdminShell } from "@/pages/AdminShell";
import { API } from "@/shop/api";
import { inr, savingsBadge } from "@/shop/products";

const IMAGE_OPTIONS = [
  "/assets/hero-bar-box.webp",
  "/assets/pack-single.webp",
  "/assets/pack-4.webp",
  "/assets/pack-8.webp",
  "/assets/product-bar-hero-dark.jpg",
  "/assets/product-bar-hero.webp",
  "/assets/product-bar-black.png",
  "/assets/product-bar-white.png",
];

const BLANK_PRODUCT = {
  title: "",
  short: "",
  description: "",
  nutrition: "",
  price: "",
  compareAt: "",
  image: "/assets/hero-bar-box.webp",
  in_stock: true,
};

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-[#7ED321]/60";
const labelCls = "mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.15em] text-white/50";

const adminToken = () => ({ headers: { "x-admin-token": localStorage.getItem("zentra-admin-token") } });

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className={labelCls}>{label}</label>
    <input data-testid={testid} className={inputCls} {...props} />
  </div>
);

const ProductEditor = ({ product, isNew, onSaved, onCancel, onDeleted }) => {
  const [form, setForm] = useState(isNew ? BLANK_PRODUCT : product);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cardId = isNew ? "new" : product.slug;

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Give the product a name first.");
      return;
    }
    const payload = {
      title: form.title.trim(),
      short: form.short || "",
      description: form.description || "",
      nutrition: form.nutrition || "",
      price: parseInt(form.price, 10) || 0,
      compareAt: form.compareAt ? parseInt(form.compareAt, 10) : null,
      image: form.image,
      in_stock: !!form.in_stock,
    };
    setSaving(true);
    try {
      if (isNew) {
        const r = await axios.post(`${API}/admin/products`, payload, adminToken());
        toast.success(`${r.data.title} added — it's live on the shop and ads feed.`);
      } else {
        await axios.put(`${API}/admin/products/${product.slug}`, payload, adminToken());
        toast.success(`${payload.title} saved — shop and ads feed updated.`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save product.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${product.title}" from the shop? Past orders keep their records.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${product.slug}`, adminToken());
      toast.success(`${product.title} removed from the shop and ads feed.`);
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove product.");
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await axios.post(`${API}/admin/products/upload-image`, fd, adminToken());
      set("image", r.data.path);
      toast.success("Photo uploaded — save the product to use it.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const badge = savingsBadge({ price: parseInt(form.price, 10) || 0, compareAt: parseInt(form.compareAt, 10) || null });
  const customImage = form.image && !IMAGE_OPTIONS.includes(form.image);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#121212] p-5" data-testid={`product-card-${cardId}`}>
      <div className="flex items-start gap-4">
        <img
          src={form.image}
          alt={form.title || "New product"}
          className="h-24 w-24 shrink-0 rounded-xl border border-white/10 bg-[#0c0e0b] object-contain p-2"
          data-testid={`product-image-preview-${cardId}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3">
            <h2 className="font-display text-lg uppercase tracking-wide text-white">
              {isNew ? "New product" : product.sku}
            </h2>
            {badge && <span className="text-[11px] font-bold text-[#7ED321]" data-testid={`product-badge-${cardId}`}>{badge}</span>}
            {!form.in_stock && <span className="text-[11px] font-bold text-[#f3a6a6]">Out of stock</span>}
          </div>
          <p className="mt-0.5 text-xs text-white/40">
            {isNew
              ? "Link and SKU are created automatically from the name when you save."
              : "SKU and link are fixed — orders, tracking and the ads feed always match."}
          </p>
        </div>
        <button
          onClick={() => set("in_stock", !form.in_stock)}
          data-testid={`product-stock-toggle-${cardId}`}
          className={`shrink-0 rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors ${
            form.in_stock ? "bg-[#7ED321]/15 text-[#7ED321]" : "bg-[#3a1215] text-[#f3a6a6]"
          }`}
        >
          {form.in_stock ? "In Stock" : "Out of Stock"}
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name" testid={`product-title-${cardId}`} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Box of 12" />
        <Field label="Weight line" testid={`product-short-${cardId}`} value={form.short || ""} onChange={(e) => set("short", e.target.value)} placeholder="4 × 50 g (200 g)" />
        <div className="sm:col-span-2">
          <label className={labelCls}>Description</label>
          <textarea
            rows={2}
            data-testid={`product-description-${cardId}`}
            className={inputCls}
            value={form.description || ""}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Nutrition line</label>
          <textarea
            rows={2}
            data-testid={`product-nutrition-${cardId}`}
            className={inputCls}
            value={form.nutrition || ""}
            onChange={(e) => set("nutrition", e.target.value)}
          />
        </div>
        <Field label="Selling price (₹)" testid={`product-price-${cardId}`} type="number" min="1" value={form.price} onChange={(e) => set("price", e.target.value)} />
        <Field label="MRP / compare price (₹, optional)" testid={`product-mrp-${cardId}`} type="number" min="0" value={form.compareAt ?? ""} onChange={(e) => set("compareAt", e.target.value)} placeholder="Leave empty for no strike-through" />
        <div className="sm:col-span-2">
          <label className={labelCls}>Photo</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select
              data-testid={`product-image-${cardId}`}
              className={inputCls}
              value={customImage ? "__custom" : form.image}
              onChange={(e) => e.target.value !== "__custom" && set("image", e.target.value)}
            >
              {IMAGE_OPTIONS.map((src) => (
                <option key={src} value={src}>{src.replace("/assets/", "")}</option>
              ))}
              {customImage && <option value="__custom">Uploaded photo (current)</option>}
            </select>
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={uploadImage} data-testid={`product-upload-input-${cardId}`} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid={`product-upload-${cardId}`}
              className="flex shrink-0 items-center justify-center gap-2 rounded-lg border border-[#7ED321]/40 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] text-[#7ED321] transition-colors hover:bg-[#7ED321]/10 disabled:opacity-50"
            >
              <Upload className="h-4 w-4" />
              {uploading ? "Uploading…" : "Upload new photo"}
            </button>
          </div>
          <p className="mt-1.5 text-[11px] text-white/35">JPG, PNG or WebP, up to 5 MB.</p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          data-testid={`product-save-${cardId}`}
          className="flex items-center gap-2 rounded-full bg-[#7ED321] px-6 py-3 text-xs font-bold uppercase tracking-[0.12em] text-[#0A0A0A] transition-colors hover:bg-[#8FE331] disabled:opacity-50"
        >
          {isNew ? <Plus className="h-4 w-4" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : isNew ? "Add Product" : `Save ${inr(parseInt(form.price, 10) || 0)}`}
        </button>
        {isNew ? (
          <button
            onClick={onCancel}
            data-testid="product-cancel-new"
            className="rounded-full border border-white/15 px-6 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white/60 transition-colors hover:text-white"
          >
            Cancel
          </button>
        ) : (
          <button
            onClick={remove}
            data-testid={`product-delete-${cardId}`}
            className="flex items-center gap-2 rounded-full border border-red-400/30 px-6 py-3 text-xs font-bold uppercase tracking-[0.12em] text-red-300 transition-colors hover:bg-red-400/10"
          >
            <Trash2 className="h-4 w-4" />
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

const ProductsManager = () => {
  const [products, setProducts] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => {
    axios
      .get(`${API}/shop/products`)
      .then((r) => setProducts(r.data))
      .catch(() => toast.error("Could not load products."));
  };
  useEffect(load, []);

  if (!products) return <p className="text-sm text-white/50">Loading products…</p>;

  return (
    <div className="space-y-5" data-testid="admin-products-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-white/50">
          Edit a pack and save — the shop, checkout and the Meta ads feed update instantly.
          Savings badges compute automatically from price and MRP.
        </p>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            data-testid="add-product-button"
            className="flex items-center gap-2 rounded-full bg-[#7ED321] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-[#0A0A0A] transition-colors hover:bg-[#8FE331]"
          >
            <Plus className="h-4 w-4" />
            Add Product
          </button>
        )}
      </div>
      {adding && (
        <ProductEditor
          isNew
          product={BLANK_PRODUCT}
          onSaved={() => { setAdding(false); load(); }}
          onCancel={() => setAdding(false)}
        />
      )}
      {products.map((p) => (
        <ProductEditor key={p.slug} product={p} onSaved={load} onDeleted={load} />
      ))}
    </div>
  );
};

const AdminProducts = () => (
  <RequireAdmin>
    <AdminShell title="Products">
      <ProductsManager />
    </AdminShell>
  </RequireAdmin>
);

export default AdminProducts;
